import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { memo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@doger/ui/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@doger/ui/components/ui/dialog';
import { $$toast } from '@doger/ui/components/ui/toast';

import type { FindOneUserResponse } from '@doger/admin/shared/api';
import { $$privateApi } from '@doger/admin/shared/api';

import styles from './delete-user-button.module.scss';

type DeleteUserButtonProps = {
  user: FindOneUserResponse;
};

export const DeleteUserButton = memo<DeleteUserButtonProps>(({ user }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const deleteUserMutation = useMutation({
    mutationFn: ({ userId }: { userId: string }) =>
      $$privateApi.deleteUser({ userId }),

    onSuccess: () => {
      // Navigate back to users list
      navigate('/users');

      // Invalidate and refetch users list
      queryClient.invalidateQueries({ queryKey: ['users'] });

      setIsOpen(false);

      $$toast.success(`User ${user.email} has been deleted successfully.`);
    },
    onError: (error) => {
      console.error(error);

      $$toast.error(`Failed to delete user`, {
        description: axios.isAxiosError(error)
          ? error.response?.data.message
          : 'An error occurred while deleting the user. Please try again.',
        style: {
          whiteSpace: 'pre-wrap',
        },
      });
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          className={styles.deleteUserButton}
          variant='outlined'
          size='md'
          palette='primary'
          disabled={deleteUserMutation.isPending}
        >
          Delete User
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete User</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this user? This action cannot be
            undone and will permanently remove the user and all their data from
            the system.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className={styles.dialogFooter}>
          <Button
            variant='outlined'
            palette='ghost'
            onClick={() => {
              setIsOpen(false);
              $$toast.info('User deletion cancelled', {
                description: 'No changes were made to the user.',
              });
            }}
            disabled={deleteUserMutation.isPending}
            size='lg'
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            palette='primary'
            onClick={() => {
              deleteUserMutation.mutate({
                userId: user.id,
              });
            }}
            disabled={deleteUserMutation.isPending}
            size='lg'
            className={styles.deleteButton}
          >
            {deleteUserMutation.isPending ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
